import React, { useEffect, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  SafeAreaView,
  TextInput,
  TouchableOpacity,
  ActivityIndicator,
  Image,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { formatDistanceToNow, parseISO } from 'date-fns';
import { LinearGradient } from 'expo-linear-gradient';
import { Linking } from 'react-native';

const BrandActivityFeed = ({ navigation }) => {
  const [activity, setActivity] = useState([]);
  const [filteredActivity, setFilteredActivity] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  // NEW: session + auth state for sidenav
  const [userData, setUserData] = useState({});
  const [isAuthed, setIsAuthed] = useState(false);
  const [authMode, setAuthMode] = useState('none'); // "subscriber" | "none"
  const [authChecked, setAuthChecked] = useState(false);
  const isSubscriber = authMode === 'subscriber';

  useEffect(() => {
    fetch('https://collabbayadmin.com/APIControllers/BrandActivity.php', {
      credentials: 'include',
    })
      .then((res) => res.json())
      .then((data) => {
        setActivity(data);
        setFilteredActivity(data);
        setLoading(false);
      })
      .catch((err) => {
        console.error('Failed to fetch activity:', err);
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    fetch('https://collabbayadmin.com/APIControllers/GetSessionData.php', {
      credentials: 'include',
    })
      .then((response) => response.json())
      .then((data) => {
        setUserData(data);
      })
      .catch((error) => {
        console.error('Error fetching user data:', error);
      });
  }, []);

  // Subscription/auth state for sidenav
  useEffect(() => {
    fetch('https://collabbayadmin.com/APIControllers/UserSubscriptionAuth.php', {
      method: 'GET',
      credentials: 'include',
    })
      .then((res) => res.json())
      .then((data) => {
        const authed =
          typeof data.authenticated === 'boolean'
            ? data.authenticated
            : data.status === 'active';
        const mode = data.auth_mode
          ? data.auth_mode
          : data.status === 'active'
          ? 'subscriber'
          : 'none';

        setIsAuthed(!!authed);
        setAuthMode(mode);
      })
      .catch((err) => console.error('Error fetching auth state:', err))
      .finally(() => setAuthChecked(true));
  }, []);

  // ---- HELPERS ----
  const openInSameTab = (url) => {
    if (typeof window !== 'undefined') {
      window.location.href = url;
    } else {
      Linking.openURL(url);
    }
  };

  const handleSearch = (query) => {
    setSearchQuery(query);
    if (!query) {
      setFilteredActivity(activity);
    } else {
      const filtered = activity.filter((item) =>
        item.Promoter.toLowerCase().includes(query.toLowerCase())
      );
      setFilteredActivity(filtered);
    }
  };

  const formatTime = (timestamp) => {
    try {
      return `Updated ${formatDistanceToNow(parseISO(timestamp), {
        addSuffix: true,
      })}`;
    } catch {
      return `Updated ${timestamp}`;
    }
  };

  const getStatusLabel = (item) => {
    const today = new Date();
    const creditAvailableDate = new Date(item.CreditAvailableStart);

    if (creditAvailableDate < today) {
      if (item.Redeemed === '1') {
        return 'Redeemed';
      } else {
        return 'Expired';
      }
    } else {
      if (item.Status === 'Accepted') {
        return 'Upcoming';
      } else if (item.Status === 'Pending') {
        return 'Pending';
      }
    }
    return 'Other';
  };

  const getStatusStyle = (status) => {
    switch (status) {
      case 'Upcoming':
        return { backgroundColor: '#d4f5d4', color: 'black' };
      case 'Redeemed':
        return { backgroundColor: '#228B22', color: 'white' };
      case 'Pending':
        return { backgroundColor: '#fff89a', color: 'black' };
      case 'Expired':
        return { backgroundColor: '#e0e0e0', color: 'black' };
      default:
        return { backgroundColor: '#ccc', color: 'black' };
    }
  };

  const renderIcon = (type) => {
    switch (type) {
      case 'New Request':
        return (
          <Ionicons
            name="mail-unread-outline"
            size={20}
            color="black"
          />
        );
      case 'Message':
        return (
          <Ionicons
            name="chatbox-ellipses-outline"
            size={20}
            color="black"
          />
        );
      case 'Date Changed':
        return (
          <Ionicons
            name="calendar-outline"
            size={20}
            color="black"
          />
        );
      case 'Accepted':
        return (
          <Ionicons
            name="checkmark-done-circle-outline"
            size={20}
            color="black"
          />
        );
      default:
        return (
          <Ionicons
            name="notifications-outline"
            size={20}
            color="black"
          />
        );
    }
  };

  const renderCard = (item, index) => {
    const status = getStatusLabel(item);
    const statusStyle = getStatusStyle(status);

    const isSentByBrand = item.LastUpdateFrom === item.BrandName;
    const direction = isSentByBrand
      ? `To: ${item.Promoter}`
      : `From: ${item.Promoter}`;

    return (
      <TouchableOpacity
        key={index}
        style={styles.activityCard}
        onPress={() =>
          navigation.navigate('BookingRequest', {
            CollabID: item.CollabID,
            openChatModal: true,
          })
        }
        activeOpacity={0.9}
      >
        <View style={styles.cardHeader}>
          {renderIcon(item.ActivityType)}
          <View style={{ flex: 1, marginLeft: 10 }}>
            <Text style={styles.promoterText}>{item.Promoter}</Text>
            <Text style={styles.subjectText}>{item.ActivityType}</Text>
          </View>
          <Text style={styles.timeText}>{formatTime(item.Date)}</Text>
        </View>

        <Text style={styles.metaText}>{direction}</Text>

        <View style={styles.cardFooterRow}>
          <Text
            style={[
              styles.statusLabel,
              {
                backgroundColor: statusStyle.backgroundColor,
                color: statusStyle.color,
              },
            ]}
          >
            {status}
          </Text>
          <Ionicons
            name="chatbox-outline"
            size={22}
            color="black"
            style={{ marginLeft: 10 }}
          />
        </View>
      </TouchableOpacity>
    );
  };

  const handleBackPress = () => {
    navigation.goBack();
  };

  // Reusable sidenav (EXACT same style/logic as BrandCalendar)
  const SideNav = () => (
    <View style={styles.sideNav}>
      <LinearGradient
        colors={['#050509', '#050505', '#000000']}
        style={styles.sideNavInner}
      >
        {/* subtle glow blob */}
        <LinearGradient
          colors={['rgba(253,240,93,0.08)', 'rgba(0,0,0,0)']}
          style={styles.sideNavGlow}
          start={{ x: 0.1, y: 0 }}
          end={{ x: 1, y: 1 }}
        />

        {/* TOP: Logo + wordmark */}
        <View style={styles.sideNavHeader}>
          <Image
            source={{
              uri: 'https://collabbayadmin.com/CollabbayLogoDark.png',
            }}
            style={styles.sideNavLogoImage}
            resizeMode="contain"
          />
          <View>
            <Text style={styles.logoWordmark}>
              <Text style={{ fontWeight: '300' }}>collab</Text>
              bay
              <Text style={styles.logoDot}>.</Text>
            </Text>
            <Text style={styles.logoDesktopTag}>DESKTOP</Text>
          </View>
        </View>

        {/* MAIN NAV SECTION */}
        <View style={styles.sideNavSection}>
          {/* Username pill */}
          <LinearGradient
            colors={['#111111', '#2b2b2b']}
            start={{ x: 0, y: 0 }}
            end={{ x: 1, y: 0 }}
            style={styles.usernamePill}
          >
            <Text
              numberOfLines={1}
              ellipsizeMode="tail"
              style={styles.usernamePillText}
            >
              {userData.Username ? ` ${userData.Username}` : 'Welcome Back'}
            </Text>
          </LinearGradient>

          {/* Home → MyPanel */}
          <TouchableOpacity
            style={styles.navItem}
            onPress={() => navigation.navigate('MyPanel')}
          >
            <View style={styles.navItemRow}>
              <View style={styles.navItemMainLabel}>
                <Ionicons
                  name="home-outline"
                  size={20}
                  color="#f5f5f5"
                  style={styles.navIcon}
                />
                <Text style={styles.navText}>Home</Text>
              </View>
            </View>
          </TouchableOpacity>

          {/* My Offers → MyOffers */}
          <TouchableOpacity
            style={styles.navItem}
            onPress={() => navigation.navigate('MyOffers')}
          >
            <View style={styles.navItemRow}>
              <View style={styles.navItemMainLabel}>
                <Ionicons
                  name="pricetags-outline"
                  size={20}
                  color="#f5f5f5"
                  style={styles.navIcon}
                />
                <Text style={styles.navText}>My Offers</Text>
              </View>
            </View>
          </TouchableOpacity>

          {/* My Calendar → MyCalendar (LOCKED if NOT authed) */}
          <TouchableOpacity
            style={styles.navItem}
            onPress={() => {
              if (authChecked && !isAuthed) {
                openInSameTab('https://collabbayadmin.com/subscribe');
              } else {
                navigation.navigate('MyCalendar');
              }
            }}
          >
            <View style={styles.navItemRow}>
              <View style={styles.navItemMainLabel}>
                <Ionicons
                  name="calendar-outline"
                  size={20}
                  color="#f5f5f5"
                  style={styles.navIcon}
                />
                <Text style={styles.navText}>My Calendar</Text>
              </View>
              {authChecked && !isAuthed && (
                <Ionicons
                  name="lock-closed-outline"
                  size={16}
                  color="#FDF05D"
                  style={styles.lockIcon}
                />
              )}
            </View>
          </TouchableOpacity>

          {/* Browse Creators → BrowseCreators */}
          <TouchableOpacity
            style={styles.navItem}
            onPress={() => navigation.navigate('BrowseCreators')}
          >
            <View style={styles.navItemRow}>
              <View style={styles.navItemMainLabel}>
                <Ionicons
                  name="people-outline"
                  size={20}
                  color="#f5f5f5"
                  style={styles.navIcon}
                />
                <Text style={styles.navText}>Browse Creators</Text>
              </View>
            </View>
          </TouchableOpacity>

          {/* Activity → ActivityFeed (LOCKED if NOT authed) */}
          <TouchableOpacity
            style={styles.navItem}
            onPress={() => {
              if (authChecked && !isAuthed) {
                openInSameTab('https://collabbayadmin.com/subscribe');
              } else {
                navigation.navigate('ActivityFeed');
              }
            }}
          >
            <View style={styles.navItemRow}>
              <View style={styles.navItemMainLabel}>
                <Ionicons
                  name="chatbubbles-outline"
                  size={20}
                  color="#f5f5f5"
                  style={styles.navIcon}
                />
                <Text style={styles.navText}>Activity</Text>
              </View>
              {authChecked && !isAuthed && (
                <Ionicons
                  name="lock-closed-outline"
                  size={16}
                  color="#FDF05D"
                  style={styles.lockIcon}
                />
              )}
            </View>
          </TouchableOpacity>

          {/* Account → BusinessAccountPage */}
          <TouchableOpacity
            style={styles.navItem}
            onPress={() => navigation.navigate('BusinessAccountPage')}
          >
            <View style={styles.navItemRow}>
              <View style={styles.navItemMainLabel}>
                <Ionicons
                  name="person-circle-outline"
                  size={20}
                  color="#f5f5f5"
                  style={styles.navIcon}
                />
                <Text style={styles.navText}>Account</Text>
              </View>
            </View>
          </TouchableOpacity>
        </View>

        {/* MIDDLE SECTION: Peer Map + Subscribe CTA */}
        <View style={styles.sideNavSection}>
          {/* Peer Map */}
          <TouchableOpacity
            style={styles.peerMapCard}
            activeOpacity={0.9}
            onPress={() => openInSameTab('/venues')}
          >
            <LinearGradient
              colors={['rgba(255,255,255,0.08)', 'rgba(0,0,0,0.4)']}
              style={styles.peerMapInner}
              start={{ x: 0, y: 0 }}
              end={{ x: 1, y: 1 }}
            >
              <View style={styles.peerMapContent}>
                <Ionicons
                  name="map-outline"
                  size={18}
                  color="#FDF05D"
                  style={{ marginRight: 8 }}
                />
                <View style={{ flex: 1 }}>
                  <Text style={styles.peerMapTitle}>Peer Map</Text>
                  <Text style={styles.peerMapSubtitle}>
                    View 100+ venues on Collabbay
                  </Text>
                </View>
              </View>
            </LinearGradient>
          </TouchableOpacity>

          {/* Subscribe CTA — only if NOT subscriber */}
          {!isSubscriber && (
            <TouchableOpacity
              style={styles.subscribeCard}
              activeOpacity={0.9}
              onPress={() =>
                openInSameTab('https://collabbayadmin.com/subscribe')
              }
            >
              <LinearGradient
                colors={['#FDF05D', '#fbbf24']}
                style={styles.subscribeGradient}
                start={{ x: 0, y: 0 }}
                end={{ x: 1, y: 1 }}
              >
                <View style={styles.subscribeContent}>
                  <Ionicons
                    name="rocket-outline"
                    size={20}
                    color="#000"
                    style={{ marginRight: 8 }}
                  />
                  <View style={{ flex: 1 }}>
                    <Text style={styles.subscribeTitle}>
                      Subscribe & unlock
                    </Text>
                    <Text style={styles.subscribeSubtitle}>
                      Unlock Activity & Calendar, plus more.
                    </Text>
                  </View>
                </View>
              </LinearGradient>
            </TouchableOpacity>
          )}
        </View>

        {/* BOTTOM: FAQ + Help */}
        <View style={styles.sideNavBottom}>
          <TouchableOpacity
            style={styles.bottomLink}
            onPress={() => openInSameTab('https://collabbayadmin.com/FAQs')}
          >
            <Ionicons
              name="help-circle-outline"
              size={18}
              color="#f5f5f5"
              style={{ marginRight: 8 }}
            />
            <View>
              <Text style={styles.bottomLinkText}>FAQs</Text>
              <Text style={styles.bottomLinkCaption}>
                Common questions answered
              </Text>
            </View>
          </TouchableOpacity>

          <TouchableOpacity
            style={styles.bottomLink}
            onPress={() =>
              Linking.openURL('mailto:info@collab-bay.com')
            }
          >
            <Ionicons
              name="headset-outline"
              size={18}
              color="#f5f5f5"
              style={{ marginRight: 8 }}
            />
            <View>
              <Text style={styles.bottomLinkText}>Help</Text>
              <Text style={styles.bottomLinkCaption}>
                We&apos;re here 24/7
              </Text>
            </View>
          </TouchableOpacity>
        </View>
      </LinearGradient>
    </View>
  );

  // ⏳ LOADING STATE — desktop shell + sidenav
  if (loading) {
    return (
      <View style={styles.root}>
        <SafeAreaView style={styles.safeArea}>
          <View style={styles.shellRow}>
            <SideNav />
            <View style={styles.mainArea}>
              <View style={[styles.loadingCenter, { flex: 1 }]}>
                <ActivityIndicator size="large" color="#111827" />
                <Text style={styles.loadingText}>Loading activity…</Text>
              </View>
            </View>
          </View>
        </SafeAreaView>
      </View>
    );
  }

  // MAIN RENDER — sidenav + inbox-style activity feed
  return (
    <View style={styles.root}>
      <SafeAreaView style={styles.safeArea}>
        <View style={styles.shellRow}>
          {/* SIDENAV */}
          <SideNav />

          {/* MAIN AREA */}
          <View style={styles.mainArea}>
            <ScrollView
              contentContainerStyle={styles.scrollContent}
              showsVerticalScrollIndicator={false}
            >
              <View style={styles.pageInner}>
                {/* HEADER / TOP ROW */}
                <View style={styles.pageHeaderRow}>
                  <TouchableOpacity
                    style={styles.backButton}
                    onPress={handleBackPress}
                  >
                    <Ionicons
                      name="arrow-back"
                      size={20}
                      color="black"
                    />
                    <Text style={styles.backButtonText}>Back</Text>
                  </TouchableOpacity>

                  <View style={styles.bannerRight}>
                    <Text style={styles.smallWelcome}>
                      {userData.Username
                        ? `Hi, ${userData.Username}`
                        : 'Brand account'}
                    </Text>
                  </View>
                </View>

                {/* PAGE TITLE + SUBTITLE */}
                <View style={{ marginBottom: 12 }}>
                  <Text style={styles.pageTitle}>Activity feed</Text>
                  <Text style={styles.pageSubtitle}>
                    Your latest booking requests and messages, in one place.
                  </Text>
                </View>

                {/* BANNER CARD (LIGHT INTRO) */}
                <View style={styles.bannerCard}>
                  <View>
                    <Text style={styles.bannerTitle}>
                      Recent Acticity
                    </Text>
                    <Text style={styles.bannerSubtitle}>
                      Tap any thread to open the full collab and chat.
                    </Text>
                  </View>
                  <Ionicons
                    name="chatbubbles-outline"
                    size={28}
                    color="black"
                  />
                </View>

                {/* SEARCH BAR */}
                <View style={styles.searchRow}>
                  <View style={styles.searchWrapper}>
                    <Ionicons
                      name="search-outline"
                      size={18}
                      color="#9ca3af"
                      style={styles.searchIcon}
                    />
                    <TextInput
                      style={styles.searchInput}
                      placeholder="Search by creator name"
                      placeholderTextColor="#9ca3af"
                      value={searchQuery}
                      onChangeText={handleSearch}
                    />
                  </View>
                </View>

                {/* ACTIVITY LIST */}
                {filteredActivity.length === 0 ? (
                  <Text style={styles.emptyText}>
                    No activity found for this view.
                  </Text>
                ) : (
                  <View style={{ width: '100%' }}>
                    {filteredActivity.map((item, index) =>
                      renderCard(item, index)
                    )}
                  </View>
                )}
              </View>
            </ScrollView>
          </View>
        </View>
      </SafeAreaView>
    </View>
  );
};

const styles = StyleSheet.create({
  // SHELL
  root: {
    flex: 1,
    backgroundColor: '#f3f4f6',
  },
  safeArea: {
    flex: 1,
  },
  shellRow: {
    flex: 1,
    flexDirection: 'row',
  },

  // MAIN AREA
  mainArea: {
    flex: 1,
    backgroundColor: '#f3f3f3',
  },
  scrollContent: {
    paddingVertical: 16,
    paddingHorizontal: 16,
    alignItems: 'center',
    alignSelf: 'center',
  },

  // LOADING
  loadingCenter: {
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    marginTop: 8,
    color: '#555',
    fontSize: 14,
  },

  // PAGE INNER
  pageInner: {
    width: '100%',
    maxWidth: 1100,
    alignSelf: 'center',
  },

  // HEADER / BANNER
  pageHeaderRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 12,
  },
  backButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#fff',
    paddingVertical: 8,
    paddingHorizontal: 14,
    borderRadius: 999,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.06,
    shadowRadius: 2,
    elevation: 2,
  },
  backButtonText: {
    marginLeft: 6,
    fontSize: 13,
    color: '#333',
    fontWeight: '500',
  },
  bannerRight: {
    alignItems: 'flex-end',
  },
  smallWelcome: {
    fontSize: 12,
    color: '#555',
  },

  pageTitle: {
    fontSize: 22,
    fontWeight: '700',
    color: '#111827',
  },
  pageSubtitle: {
    fontSize: 12,
    color: '#6b7280',
    marginTop: 2,
  },

  bannerCard: {
    backgroundColor: '#FDF05D',
    borderRadius: 16,
    padding: 16,
    marginBottom: 16,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  bannerTitle: {
    fontSize: 18,
    fontWeight: '700',
    color: '#111',
    marginBottom: 4,
  },
  bannerSubtitle: {
    fontSize: 13,
    color: '#333',
  },

  // SEARCH
  searchRow: {
    marginBottom: 12,
  },
  searchWrapper: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#f9fafb',
    borderRadius: 999,
    borderWidth: 1,
    borderColor: '#e5e7eb',
    paddingHorizontal: 10,
    paddingVertical: 6,
  },
  searchIcon: {
    marginRight: 6,
  },
  searchInput: {
    flex: 1,
    fontSize: 13,
    color: '#111827',
    paddingVertical: 4,
  },

  // ACTIVITY CARD
  activityCard: {
    backgroundColor: '#ffffff',
    padding: 15,
    marginVertical: 6,
    borderRadius: 12,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.06,
    shadowRadius: 4,
    elevation: 2,
    borderWidth: 1,
    borderColor: '#e5e7eb',
  },
  cardHeader: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  promoterText: {
    fontWeight: 'bold',
    fontSize: 15,
  },
  timeText: {
    fontSize: 12,
    color: '#888',
    marginLeft: 5,
  },
  subjectText: {
    fontSize: 14,
    color: '#333',
    marginTop: 2,
  },
  metaText: {
    fontSize: 12,
    color: '#555',
    marginTop: 6,
  },
  statusLabel: {
    alignSelf: 'flex-start',
    paddingVertical: 3,
    paddingHorizontal: 10,
    borderRadius: 15,
    fontSize: 12,
    marginTop: 6,
  },
  cardFooterRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: 8,
  },

  emptyText: {
    textAlign: 'center',
    color: '#555',
    marginTop: 30,
    fontSize: 15,
  },

  // SIDE NAV (EXACT COPY FROM BrandCalendar)
  sideNav: {
    width: 250,
    backgroundColor: '#000',
  },
  sideNavInner: {
    flex: 1,
    paddingVertical: 24,
    paddingHorizontal: 16,
    borderTopRightRadius: 20,
    borderBottomRightRadius: 20,
    overflow: 'hidden',
    position: 'relative',
  },
  sideNavGlow: {
    position: 'absolute',
    top: 140,
    left: -60,
    width: 260,
    height: 260,
    borderRadius: 130,
    opacity: 0.55,
  },
  sideNavHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 26,
    zIndex: 2,
  },
  sideNavLogoImage: {
    width: 38,
    height: 38,
    marginRight: 10,
  },
  logoWordmark: {
    fontSize: 16,
    color: '#f5f5f5',
    fontWeight: '600',
    letterSpacing: 0.3,
  },
  logoDot: {
    color: '#FDF05D',
    fontSize: 18,
    fontWeight: '800',
  },
  logoDesktopTag: {
    fontSize: 9,
    color: '#b3b3b3',
    letterSpacing: 2,
    marginTop: 2,
  },
  sideNavSection: {
    marginTop: 12,
    marginBottom: 20,
    zIndex: 2,
  },
  usernamePill: {
    paddingTop: 4,
    paddingBottom: 8,
    paddingHorizontal: 10,
    borderRadius: 999,
    alignSelf: 'flex-start',
    maxWidth: '100%',
    marginLeft: 1,
    marginBottom: 10,
  },
  usernamePillText: {
    fontSize: 8,
    color: '#ffffff',
    textTransform: 'uppercase',
    letterSpacing: 1,
    textAlign: 'left',
  },
  navItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 9,
    paddingHorizontal: 8,
    borderRadius: 10,
    marginBottom: 6,
  },
  navIcon: {
    marginRight: 10,
  },
  navItemRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    flex: 1,
  },
  navItemMainLabel: {
    flexDirection: 'row',
    alignItems: 'center',
    flexShrink: 1,
  },
  navText: {
    color: '#f5f5f5',
    fontSize: 14,
    fontWeight: '500',
  },
  lockIcon: {
    marginLeft: 6,
  },

  // Peer Map card
  peerMapCard: {
    borderRadius: 14,
    overflow: 'hidden',
    marginTop: 4,
    marginBottom: 10,
  },
  peerMapInner: {
    paddingVertical: 10,
    paddingHorizontal: 10,
    borderRadius: 14,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.08)',
  },
  peerMapContent: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  peerMapTitle: {
    color: '#f5f5f5',
    fontSize: 13,
    fontWeight: '600',
  },
  peerMapSubtitle: {
    color: '#b3b3b3',
    fontSize: 11,
    marginTop: 2,
  },

  // Subscribe CTA
  subscribeCard: {
    borderRadius: 14,
    overflow: 'hidden',
    marginTop: 10,
  },
  subscribeGradient: {
    paddingVertical: 10,
    paddingHorizontal: 12,
  },
  subscribeContent: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  subscribeTitle: {
    fontSize: 13,
    fontWeight: '700',
    color: '#000',
  },
  subscribeSubtitle: {
    fontSize: 11,
    color: '#1f2933',
    marginTop: 2,
  },

  // Bottom links
  sideNavBottom: {
    marginTop: 'auto',
    paddingTop: 12,
    borderTopWidth: 1,
    borderTopColor: '#181818',
    zIndex: 2,
  },
  bottomLink: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 6,
  },
  bottomLinkText: {
    fontSize: 13,
    color: '#f5f5f5',
    fontWeight: '600',
  },
  bottomLinkCaption: {
    fontSize: 11,
    color: '#b3b3b3',
    marginTop: 2,
  },
});

export default BrandActivityFeed;
